import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { Button } from "react-bootstrap";
import {
  transformToInsuredMembers,
  transformToInsuredMembersForPutQuote,
} from "../../utils/gigHelpers";
import { getQuote } from "../../features/product";
import { useDispatch, useSelector } from "react-redux";
import { Loader } from "../common/loader";
import Stepper from "../../components/Stepper.js";
import SelectDropdown from "../SelectDropdown";

const ageCategories = [
  { key: "no_of_adults_-_(age_16_to_64)", labelKey: "gig.personDetails.ageGroups.adults" },
  { key: "no_of_child_-_(age_0_to_15)", labelKey: "gig.personDetails.ageGroups.children" },
  { key: "no_of_seniors_-_(age_65_to_69)", labelKey: "gig.personDetails.ageGroups.seniors" },
];

// Relationship options with code mapping
const getRelationshipOptions = (t) => [
  { code: "CH", labelKey: "gig.personDetails.relationshipOptions.child", text: t("gig.personDetails.relationshipOptions.child") },
  { code: "PA", labelKey: "gig.personDetails.relationshipOptions.parent", text: t("gig.personDetails.relationshipOptions.parent") },
  { code: "CO", labelKey: "gig.personDetails.relationshipOptions.colleague", text: t("gig.personDetails.relationshipOptions.colleague") },
  { code: "OR", labelKey: "gig.personDetails.relationshipOptions.otherRelatives", text: t("gig.personDetails.relationshipOptions.otherRelatives") },
  { code: "SP", labelKey: "gig.personDetails.relationshipOptions.spouse", text: t("gig.personDetails.relationshipOptions.spouse") },
  { code: "OT", labelKey: "gig.personDetails.relationshipOptions.other", text: t("gig.personDetails.relationshipOptions.other") },
  { code: "PI", labelKey: "gig.personDetails.relationshipOptions.self", text: t("gig.personDetails.relationshipOptions.self") }
];

const normalizePrefillCounts = (data) => {
  const keyMap = {
    "no_of_adults_-_(age_18_or_above)": "no_of_adults_-_(age_16_to_64)",
    "no_of_child_-_(age_16_or_below)": "no_of_child_-_(age_0_to_15)",
    "no_of_child_-_(age_17_to_18)": "no_of_child_-_(age_17_to_18)",
    "no_of_seniors_-_(age_71_to_79)": "no_of_seniors_-_(age_65_to_69)",
  };

  const normalized = {};

  Object.entries(data || {}).forEach(([key, value]) => {
    const mappedKey = keyMap[key];
    if (mappedKey) {
      normalized[mappedKey] = value;
    }
  });

  return normalized;
};

const getAgeGroupRanges = () => {
  const today = new Date();
  const getDateYearsAgo = (years) => {
    const date = new Date(today);
    date.setFullYear(date.getFullYear() - years);
    return date.toISOString().split("T")[0];
  };

  return {
    "no_of_adults_-_(age_16_to_64)": {
      min: getDateYearsAgo(64),
      max: getDateYearsAgo(16),
    },
    "no_of_child_-_(age_0_to_15)": {
      min: getDateYearsAgo(15),
      max: getDateYearsAgo(0),
    },
    "no_of_seniors_-_(age_65_to_69)": {
      min: getDateYearsAgo(69),
      max: getDateYearsAgo(65),
    },
  };
};

const GigPersonDetailsForm = () => {
  const { t } = useTranslation(); // ✅ Add translation hook
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const planData = useMemo(
    () => location?.state?.data || {},
    [location?.state]
  );

  const [formData, setFormData] = useState({});
  const [people, setPeople] = useState([]);
  const [errors, setErrors] = useState({});
  const { quoteLoader } = useSelector((state) => state.product);

  const primaryUser = people && people.length ? people[0]?.person : [];
  const productDataStr = localStorage.getItem("product_inquiry");
  const productData = JSON.parse(productDataStr);
  const prefilledForms = JSON.parse(productData.form_data);
  const emailIds = prefilledForms.email;

  const prefillCounts = useMemo(() => {
    const raw = location?.state?.prefillCounts || {};
    return normalizePrefillCounts(raw);
  }, [location?.state]);

  const adults = Number(formData?.["no_of_adults_-_(age_16_to_64)"] || 0);
  const children = Number(formData?.["no_of_child_-_(age_0_to_15)"] || 0);
  const seniors = Number(formData?.["no_of_seniors_-_(age_65_to_69)"] || 0);

  const disableSenior = adults > 0 || children > 0;
  const disableAdultsAndChildren = seniors > 0;

  // ✅ Get translated relationship options
  const relationshipOptions = getRelationshipOptions(t);

  // Validation functions
  const isValidEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validatePersonField = (personObj, index) => {
    const personErrors = {};
    const fieldKey = `person_${index}`;

    // Required field validations
    if (!personObj.person.givenName || personObj.person.givenName.trim() === "") {
      personErrors.givenName = t("gig.personDetails.validationMessages.givenNameRequired");
    }

    if (!personObj.person.surName || personObj.person.surName.trim() === "") {
      personErrors.surName = t("gig.personDetails.validationMessages.surnameRequired");
    }

    if (!personObj.person.birthDate || personObj.person.birthDate.trim() === "") {
      personErrors.birthDate = t("gig.personDetails.validationMessages.birthDateRequired");
    } else {
      // Validate age is within range
      const selectedDate = new Date(personObj.person.birthDate);
      const minDate = new Date(personObj.dateLimits.min);
      const maxDate = new Date(personObj.dateLimits.max);

      if (selectedDate < minDate || selectedDate > maxDate) {
        personErrors.birthDate = `${t("gig.personDetails.validationMessages.birthDateInvalid")} ${personObj.dateLimits.min} ${t("gig.personDetails.dateRange.to")} ${personObj.dateLimits.max}`;
      }
    }

    if (!personObj.person.gender.code || personObj.person.gender.code === "") {
      personErrors.gender = t("gig.personDetails.validationMessages.genderRequired");
    }

    if (!personObj.person.relationship.code || personObj.person.relationship.code === "") {
      personErrors.relationship = t("gig.personDetails.validationMessages.relationshipRequired");
    }

    // Email validation for primary person (index 0)
    if (index === 0) {
      if (!personObj.person.email || personObj.person.email.trim() === "") {
        personErrors.email = t("gig.personDetails.validationMessages.emailRequired");
      } else if (!isValidEmail(personObj.person.email)) {
        personErrors.email = t("gig.personDetails.validationMessages.emailInvalid");
      }

      if (!personObj.person.national_id || personObj.person.national_id.trim() === "") {
        personErrors.national_id = t("gig.personDetails.validationMessages.nationalIdRequired");
      }

      if (!personObj.person.national_id_iss_date || personObj.person.national_id_iss_date.trim() === "") {
        personErrors.national_id_iss_date = t("gig.personDetails.validationMessages.nationalIdIssueRequired");
      }

      if (!personObj.person.national_id_exp_date || personObj.person.national_id_exp_date.trim() === "") {
        personErrors.national_id_exp_date = t("gig.personDetails.validationMessages.nationalIdExpiryRequired");
      }
    }

    if (Object.keys(personErrors).length > 0) {
      return { fieldKey, personErrors };
    }

    return null;
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;

    if (people.length === 0) {
      toast.error(t("gig.personDetails.validationMessages.addPeople") || "Please add at least one person");
      return false;
    }

    // Validate each person
    people.forEach((personObj, index) => {
      const validation = validatePersonField(personObj, index);
      if (validation) {
        newErrors[validation.fieldKey] = validation.personErrors;
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const clearFieldError = (personIndex, fieldName) => {
    setErrors((prevErrors) => {
      const fieldKey = `person_${personIndex}`;
      const updatedPersonErrors = { ...prevErrors[fieldKey] };
      delete updatedPersonErrors[fieldName];

      if (Object.keys(updatedPersonErrors).length === 0) {
        const { [fieldKey]: _, ...restErrors } = prevErrors;
        return restErrors;
      }

      return {
        ...prevErrors,
        [fieldKey]: updatedPersonErrors,
      };
    });
  };

  useEffect(() => {
    if (!prefillCounts || Object.keys(prefillCounts).length === 0) return;

    const ranges = getAgeGroupRanges();
    const newPeople = [];

    setFormData(prefillCounts);

    Object.entries(prefillCounts).forEach(([categoryKey, count]) => {
      const dateLimits = ranges[categoryKey];

      if (!dateLimits) return;

      for (let i = 0; i < count; i++) {
        newPeople.push({
          person: {
            surName: "",
            givenName: "",
            birthDate: "",
            email: i == 0 ? emailIds : undefined,
            relationship: { code: "", value: "" },
            gender: { code: "", value: "" },
          },
          groupKey: categoryKey,
          dateLimits,
        });
      }
    });

    setPeople(newPeople);
  }, [prefillCounts]);

  const handleCountChange = (e, key) => {
    const value = parseInt(e.target.value) || 0;
    const newFormData = { ...formData, [key]: value };
    setFormData(newFormData);

    const ranges = getAgeGroupRanges();
    const newPeople = [];

    Object.entries(newFormData).forEach(([categoryKey, count]) => {
      for (let i = 0; i < count; i++) {
        newPeople.push({
          person: {
            surName: "",
            givenName: "",
            birthDate: "",
            email: i == 0 ? emailIds : undefined,
            relationship: { code: "", value: "" },
            gender: { code: "", value: "" },
          },
          groupKey: categoryKey,
          dateLimits: ranges[categoryKey],
        });
      }
    });

    setPeople(newPeople);
    setErrors({}); // Clear errors when count changes
  };

  const handlePersonChange = (index, field, value) => {
    const updatedPeople = [...people];
    if (field === "gender") {
      updatedPeople[index].person.gender =
        value === "M"
          ? { code: "M", value: t("gig.personDetails.formLabels.male") }
          : { code: "F", value: t("gig.personDetails.formLabels.female") };
    } else if (field === "relationship") {
      const selected = relationshipOptions.find((x) => x.code === value);

      updatedPeople[index].person.relationship = {
        code: selected.code,
        value: selected.code == "PI" ? t("gig.personDetails.relationshipOptions.self") : selected.text
      };
    } else {
      updatedPeople[index].person[field] = value;
    }
    setPeople(updatedPeople);
    clearFieldError(index, field);
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    // Validate form before submission
    if (!validateForm()) {
      console.log("Form validation failed");
      return;
    }

    const mobileNumber = prefilledForms.mobile_no;
    const primaryUserGender = primaryUser?.gender;
    const primaryRelationship = primaryUser?.relationship;
    const digits = mobileNumber;

    // Oman default
    let prefixCode = "968";
    let operatorCode = "";
    let phoneNumber = "";

    // If number already has country code
    if (digits.startsWith("968") && digits.length >= 11) {
      prefixCode = "968";
      operatorCode = digits.substring(3, 5);
      phoneNumber = digits.substring(5);
    }
    // If local Oman mobile (8 digits)
    else if (digits.length === 8) {
      operatorCode = digits.substring(0, 2);
      phoneNumber = digits.substring(2);
    }

    const policyHolder = {
      person: {
        surName: primaryUser?.surName,
        givenName: primaryUser?.givenName,
        middleName: "",
        birthDate: primaryUser?.birthDate,
        gender: {
          code: primaryUserGender?.code === "M" ? "GC02" : "GC01",
          value: primaryUserGender?.value,
        },
        relationship: {
          code: primaryRelationship?.code,
          value: primaryRelationship?.value,
        },
        nationality: {
          code: "IND",
          value: "India",
        },
      },
      contactMethod: {
        phoneContacts: [
          {
            phoneType: "MOBILE, LANDLINE",
            prefixCode: "968",
            operatorCode: operatorCode,
            phoneNumber: phoneNumber,
            internationalFullNumber: "971565630173",
          },
        ],
        emailContact: [
          {
            type: "PERSONAL, WORK",
            url: primaryUser?.email,
          },
        ],
      },
    };

    const policyHolderPut = {
      person: {
        surName: primaryUser?.surName,
        givenName: primaryUser?.givenName,
        middleName: "",
        birthDate: primaryUser?.birthDate,
        gender: {
          code: primaryUserGender?.code === "M" ? "GC02" : "GC01",
          value: primaryUserGender?.value,
        },
        relationship: {
          code: primaryRelationship?.code,
          value: primaryRelationship?.value,
        },
        nationality: {
          code: "IND",
          value: "India",
        },
      },
      contactMethod: {
        postMailContact: [
          {
            houseNr: "209",
            buildingNr: "Danube building",
            addressLine1: {
              code: "SAREA00676",
              value: "Muscat",
            },
            addressLine2: "Near Choitram Supermarket",
            city: {
              code: "OMN11",
              value: "Muscat",
            },
            country: {
              code: "OMN",
              value: "OMAN",
            },
            postalCode: "777",
            poBox: "888",
          },
        ],
        phoneContacts: [
          {
            phoneType: "MOBILE, LANDLINE",
            prefixCode: "968",
            operatorCode: operatorCode,
            phoneNumber: phoneNumber,
            internationalFullNumber: `968${operatorCode}${phoneNumber}`,
          },
        ],
        emailContact: [
          {
            type: "PERSONAL, WORK",
            url: primaryUser?.email,
          },
        ],
      },
      documents: [
        {
          docType: {
            code: "DT03",
            value: "National ID",
          },
          docId: primaryUser?.national_id || "784199909896786",
          docExpiryDate: primaryUser?.national_id_exp_date || "2025-09-19",
          docIssueDate: primaryUser?.national_id_iss_date || "2022-09-19",
          docIssuingCountry: "string",
          docReferance: "string",
          isVerified: true,
        },
      ],
      customerInformationFileNumber: "11235",
    };

    const { insuredMembers } = transformToInsuredMembers(people);
    const { insuredMembersPut } = transformToInsuredMembersForPutQuote(people);

    var selectedCoverPlan = planData?.extracted_data?.eligiblePlans.filter(
      (value) => value.product.code == planData?.plan_code
    )[0];

    const selectedPlan = {
      product: {
        code: selectedCoverPlan.product?.code,
        value: selectedCoverPlan.product?.value,
      },
      premium: selectedCoverPlan?.premium,
      originalPremium: selectedCoverPlan?.originalPremium,
      uwApprovalStatus: "Y",
      covers: planData?.included_covers
        .concat(planData?.optional_covers)
        .filter((item) => item?.isSelected === true),
    };

    dispatch(
      getQuote({
        ...planData?.extracted_data,
        policyHolder,
        policyHolderPut,
        insuredMembers,
        insuredMembersPut,
        selectedPlan,
        planData,
        premium_value: planData?.premium_value,
        premium_currency: planData?.premium_currency,
        insurance_company_product_mapping_id:
          planData?.insurance_company_product_mapping_id,
        company_id: planData?.company_id,
        primary_email: primaryUser?.email,
      })
    ).then((res) => {
      navigate("/checkout", { state: { existingData: res.payload } });
    });
  };

  useEffect(() => {
    if (disableSenior && seniors > 0) {
      setFormData((prev) => ({
        ...prev,
        ["no_of_seniors_-_(age_65_to_69)"]: 0,
      }));
    }

    if (disableAdultsAndChildren && (adults > 0 || children > 0)) {
      setFormData((prev) => ({
        ...prev,
        ["no_of_adults_-_(age_16_to_64)"]: 0,
        ["no_of_child_-_(age_0_to_15)"]: 0,
      }));
    }
  }, [disableSenior, disableAdultsAndChildren]);

  if (quoteLoader) {
    return <Loader type="logo-gif" />;
  }

  return (
    <div className="container py-4">
      <div className="policy-form why-choose-us">
        <form onSubmit={handleSubmit}>
          <h3 className="pb-3 fw-bold text-center">
            {t("gig.personDetails.title")} {/* ✅ Translated heading */}
          </h3>

          <div className="row">
            {ageCategories.map(({ key, labelKey }) => {
              const adults = Number(
                formData["no_of_adults_-_(age_16_to_64)"] || 0
              );
              const children = Number(
                formData["no_of_child_-_(age_0_to_15)"] || 0
              );
              const seniors = Number(
                formData["no_of_seniors_-_(age_65_to_69)"] || 0
              );
              const isSeniorField = key === "no_of_seniors_-_(age_65_to_69)";
              const isAdultOrChildField =
                key === "no_of_child_-_(age_0_to_15)" ||
                key === "no_of_adults_-_(age_16_to_64)";

              let isDisabled = false;

              if (isAdultOrChildField && seniors > 0) {
                isDisabled = true;
              }

              if (isSeniorField && (adults > 0 || children > 0)) {
                isDisabled = true;
              }

              return (
                <div className="col-md-6 col-sm-12" key={key}>
                  <div className="form-group">
                    <label className="form-label">
                      {t(labelKey)} {/* ✅ Translated age group label */}
                    </label>
                    <input
                      type="number"
                      className="form-control element-style"
                      min="0"
                      value={formData[key] || ""}
                      onChange={(e) => handleCountChange(e, key)}
                      disabled={isDisabled}
                    />
                  </div>
                </div>
              );
            })}
          </div>

          {people.length > 0 && <hr className="my-4" />}

          {people.map((personObj, index) => {
            const fieldKey = `person_${index}`;
            const personErrors = errors[fieldKey] || {};
            const ageCategoryLabel = ageCategories.find(
              (a) => a.key === personObj.groupKey
            )?.labelKey;

            return (
              <div className="card mb-4" key={"people_form_card_" + index}>
                <div className="card-header">
                  <strong>
                    {t("gig.personDetails.personCard.title")} {index + 1}{" "}
                    {t("gig.personDetails.personCard.separator")}{" "}
                    {ageCategoryLabel && t(ageCategoryLabel)}
                  </strong>
                  {Object.keys(personErrors).length > 0 && (
                    <span className="text-danger ms-2">
                      {t("gig.personDetails.personCard.warning")}
                    </span>
                  )}
                </div>
                <div className="card-body row g-3">
                  {/* Given Name */}
                  <div className="col-md-6">
                    <label className="form-label">
                      {t("gig.personDetails.formLabels.givenName")}{" "}
                      <span className="text-danger">
                        {t("gig.personDetails.formLabels.required")}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-control element-style ${
                        personErrors.givenName ? "is-invalid" : ""
                      }`}
                      value={personObj.person.givenName}
                      onChange={(e) =>
                        handlePersonChange(index, "givenName", e.target.value)
                      }
                    />
                    {personErrors.givenName && (
                      <div className="invalid-feedback d-block">
                        {personErrors.givenName}
                      </div>
                    )}
                  </div>

                  {/* Surname */}
                  <div className="col-md-6">
                    <label className="form-label">
                      {t("gig.personDetails.formLabels.surname")}{" "}
                      <span className="text-danger">
                        {t("gig.personDetails.formLabels.required")}
                      </span>
                    </label>
                    <input
                      type="text"
                      className={`form-control element-style ${
                        personErrors.surName ? "is-invalid" : ""
                      }`}
                      value={personObj.person.surName}
                      onChange={(e) =>
                        handlePersonChange(index, "surName", e.target.value)
                      }
                    />
                    {personErrors.surName && (
                      <div className="invalid-feedback d-block">
                        {personErrors.surName}
                      </div>
                    )}
                  </div>

                  {/* Birth Date */}
                  <div className="col-md-6">
                    <label className="form-label">
                      {t("gig.personDetails.formLabels.birthDate")}{" "}
                      <span className="text-danger">
                        {t("gig.personDetails.formLabels.required")}
                      </span>
                    </label>
                    <input
                      type="date"
                      className={`form-control element-style ${
                        personErrors.birthDate ? "is-invalid" : ""
                      }`}
                      name="birthDate"
                      min={personObj.dateLimits.min}
                      max={personObj.dateLimits.max}
                      value={personObj.person.birthDate}
                      onChange={(e) =>
                        handlePersonChange(index, "birthDate", e.target.value)
                      }
                    />
                    <small className="text-muted">
                      {t("gig.personDetails.dateRange.allowed")}{" "}
                      {personObj.dateLimits.min} {t("gig.personDetails.dateRange.to")}{" "}
                      {personObj.dateLimits.max}
                    </small>
                    {personErrors.birthDate && (
                      <div className="invalid-feedback d-block">
                        {personErrors.birthDate}
                      </div>
                    )}
                  </div>

                  {/* Gender */}
                  <div className="col-md-6">
                    <label className="form-label">
                      {t("gig.personDetails.formLabels.gender")}{" "}
                      <span className="text-danger">
                        {t("gig.personDetails.formLabels.required")}
                      </span>
                    </label>
                    <SelectDropdown
                      name="gender"
                      placeholder={t("gig.personDetails.formLabels.selectGender")}
                      options={[
                        { label: t("gig.personDetails.formLabels.male"), value: "M" },
                        { label: t("gig.personDetails.formLabels.female"), value: "F" },
                      ]}
                      defaultValue={personObj.person.gender.code ? { value: personObj.person.gender.code } : null}
                      handleOnChange={(name, value) =>
                        handlePersonChange(index, "gender", value)
                      }
                      isSearchable={false}
                    />
                    {personErrors.gender && (
                      <div className="invalid-feedback d-block">
                        {personErrors.gender}
                      </div>
                    )}
                  </div>

                  {/* Relationship */}
                  <div className="col-md-6">
                    <label className="form-label">
                      {t("gig.personDetails.formLabels.relationship")}{" "}
                      <span className="text-danger">
                        {t("gig.personDetails.formLabels.required")}
                      </span>
                    </label>
                    <SelectDropdown
                      name="relationship"
                      placeholder={t("gig.personDetails.formLabels.selectRelationship")}
                      options={relationshipOptions.map((item) => ({
                        label: item.text,
                        value: item.code,
                      }))}
                      defaultValue={personObj.person.relationship.code ? { value: personObj.person.relationship.code } : null}
                      handleOnChange={(name, value) =>
                        handlePersonChange(index, "relationship", value)
                      }
                      isSearchable={false}
                    />
                    {personErrors.relationship && (
                      <div className="invalid-feedback d-block">
                        {personErrors.relationship}
                      </div>
                    )}
                  </div>

                  {/* First person only fields */}
                  {index === 0 && (
                    <>
                      {/* Email */}
                      <div className="col-md-6">
                        <label className="form-label">
                          {t("gig.personDetails.formLabels.email")}{" "}
                          <span className="text-danger">
                            {t("gig.personDetails.formLabels.required")}
                          </span>
                        </label>
                        <input
                          type="email"
                          className={`form-control element-style ${
                            personErrors.email ? "is-invalid" : ""
                          }`}
                          name="email"
                          value={personObj.person.email}
                          onChange={(e) =>
                            handlePersonChange(index, "email", e.target.value)
                          }
                        />
                        {personErrors.email && (
                          <div className="invalid-feedback d-block">
                            {personErrors.email}
                          </div>
                        )}
                      </div>

                      {/* National ID Number */}
                      <div className="col-md-6">
                        <label className="form-label">
                          {t("gig.personDetails.formLabels.nationalIdNumber")}{" "}
                          <span className="text-danger">
                            {t("gig.personDetails.formLabels.required")}
                          </span>
                        </label>
                        <input
                          type="text"
                          className={`form-control element-style ${
                            personErrors.national_id ? "is-invalid" : ""
                          }`}
                          name="national_id"
                          value={personObj.person.national_id}
                          onChange={(e) =>
                            handlePersonChange(
                              index,
                              "national_id",
                              e.target.value
                            )
                          }
                        />
                        {personErrors.national_id && (
                          <div className="invalid-feedback d-block">
                            {personErrors.national_id}
                          </div>
                        )}
                      </div>

                      {/* National ID Issue Date */}
                      <div className="col-md-6">
                        <label className="form-label">
                          {t("gig.personDetails.formLabels.nationalIdIssueDate")}{" "}
                          <span className="text-danger">
                            {t("gig.personDetails.formLabels.required")}
                          </span>
                        </label>
                        <input
                          type="date"
                          className={`form-control element-style ${
                            personErrors.national_id_iss_date ? "is-invalid" : ""
                          }`}
                          name="national_id_iss_date"
                          max={new Date().toISOString().split("T")[0]}
                          value={personObj.person.national_id_iss_date}
                          onChange={(e) =>
                            handlePersonChange(
                              index,
                              "national_id_iss_date",
                              e.target.value
                            )
                          }
                        />
                        {personErrors.national_id_iss_date && (
                          <div className="invalid-feedback d-block">
                            {personErrors.national_id_iss_date}
                          </div>
                        )}
                      </div>

                      {/* National ID Expiry Date */}
                      <div className="col-md-6">
                        <label className="form-label">
                          {t("gig.personDetails.formLabels.nationalIdExpiryDate")}{" "}
                          <span className="text-danger">
                            {t("gig.personDetails.formLabels.required")}
                          </span>
                        </label>
                        <input
                          type="date"
                          className={`form-control element-style ${
                            personErrors.national_id_exp_date ? "is-invalid" : ""
                          }`}
                          name="national_id_exp_date"
                          value={personObj.person.national_id_exp_date}
                          onChange={(e) =>
                            handlePersonChange(
                              index,
                              "national_id_exp_date",
                              e.target.value
                            )
                          }
                        />
                        {personErrors.national_id_exp_date && (
                          <div className="invalid-feedback d-block">
                            {personErrors.national_id_exp_date}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              </div>
            );
          })}

          {people.length > 0 && (
            <div className="text-end">
              <Button type="submit" className="h-btn">
                {t("gig.personDetails.buttons.submit")}
              </Button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default GigPersonDetailsForm;